import { useEffect, useRef, useState } from "react";
import { usePinnedSidebar } from "../lib/usePinnedSidebar";

// Shared chrome for the dashboard and the Super Admin console: the left
// sidebar and the header's account menu.

export const MENU_CLASS =
  "absolute top-[44px] z-50 rounded-xl border border-[#EAECF0] bg-white p-[6px] shadow-[0_4px_8px_-2px_rgba(16,24,40,0.08),0_12px_24px_-4px_rgba(16,24,40,0.10)]";

// Open state for a header popover that closes on an outside click or Escape.
export function usePopover() {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return { open, setOpen, ref };
}

const RAIL = 72;
const PANEL = 252;
const PIN_KEY = "c360.sidebar.pinned";
// One easing for the whole sidebar, so the width, the labels and the shadow
// arrive together instead of each snapping on its own.
const EASE = "cubic-bezier(0.22, 1, 0.36, 1)";

function PinIcon({ pinned }) {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 01-1.11 1.79l-1.78.9A2 2 0 005 15.24V16a1 1 0 001 1h12a1 1 0 001-1v-.76a2 2 0 00-1.11-1.79l-1.78-.9A2 2 0 0115 10.76V7a1 1 0 011-1 2 2 0 000-4H8a2 2 0 000 4 1 1 0 011 1z" />
      {pinned && <path d="M3 3l18 18" />}
    </svg>
  );
}

// The app's left navigation, in the Chat360 style. Open (icons and labels) by
// default; the pin button folds it to an icon rail, and the choice is kept for
// next time. While folded, resting the pointer on the rail opens it over the
// page so the labels can be read; the page itself does not move.
// `groups` is [{ label, accent, items }], each item { key, label, icon (node),
// active, onClick } — or pass `renderItem` to wrap an item in a router link.
export function HoverSidebar({ groups, renderItem }) {
  const { pinned, peek, open, togglePin, handlers } = usePinnedSidebar(PIN_KEY);

  // Labels fade and slide in a beat after the panel starts to widen, and fade
  // out at once when it folds, so text is never squeezed mid-animation.
  const fade = {
    opacity: open ? 1 : 0,
    transform: open ? "translateX(0)" : "translateX(-6px)",
    transition: `opacity ${open ? 220 : 100}ms ${EASE} ${open ? 60 : 0}ms, transform 280ms ${EASE}`,
  };

  return (
    <div
      className="sticky top-0 z-40 h-screen flex-none self-start"
      style={{ width: pinned ? PANEL : RAIL, transition: `width 280ms ${EASE}` }}
    >
      <aside
        {...handlers}
        className="absolute bottom-0 left-0 top-0 flex flex-col overflow-hidden border-r border-[#ECEEF2] bg-white"
        style={{
          width: open ? PANEL : RAIL,
          transition: `width 280ms ${EASE}, box-shadow 280ms ${EASE}`,
          boxShadow: peek && !pinned ? "12px 0 32px -16px rgba(16,24,40,0.22)" : "none",
        }}
      >
        {/* The mark never moves; only the wordmark beside it fades in, so the
            logo never swaps mid-animation. */}
        <div className="flex h-16 flex-none items-center gap-2 pl-[18px] pr-3">
          <img src="/chat360-mark.png" alt="Chat360" className="h-9 w-9 flex-none" />
          <img src="/chat360-wordmark.png" alt="" aria-hidden="true" className="h-[22px] w-auto flex-none" style={fade} />
          <button
            type="button"
            onClick={togglePin}
            aria-label={pinned ? "Fold the sidebar" : "Keep the sidebar open"}
            title={pinned ? "Fold the sidebar" : "Keep the sidebar open"}
            tabIndex={open ? 0 : -1}
            className="ml-auto flex h-8 w-8 flex-none cursor-pointer items-center justify-center rounded-lg text-[#98A2B3] transition-colors duration-150 hover:bg-[#F2F4F7] hover:text-[#344054] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6]"
            style={{ ...fade, pointerEvents: open ? "auto" : "none" }}
          >
            <PinIcon pinned={pinned} />
          </button>
        </div>

        <nav
          className="scrollbar-none flex-1 overflow-y-auto overflow-x-hidden px-3 pb-4 pt-1"
          aria-label="Sections"
        >
          {groups.map((g, gi) => (
            <div key={g.label || gi} className={gi > 0 ? "mt-4" : ""}>
              {(g.label || gi > 0) && (
                <div className="relative flex h-7 items-center whitespace-nowrap px-[14px]">
                  {/* Folded, a heading is only a short rule; open, it is the
                      group's name. Both are always there and cross-fade. */}
                  <span
                    aria-hidden="true"
                    className="absolute left-[22px] top-1/2 h-px w-5 bg-[#E4E7EC]"
                    style={{ opacity: open ? 0 : 1, transition: `opacity 160ms ${EASE}` }}
                  />
                  {g.label && (
                    <span className="flex items-center gap-2" style={fade}>
                      <span aria-hidden="true" className="h-3 w-[3px] rounded-full" style={{ background: g.accent || "#98A2B3" }} />
                      <span className="text-[11px] font-semibold uppercase tracking-[0.08em]" style={{ color: g.accent || "#667085" }}>
                        {g.label}
                      </span>
                    </span>
                  )}
                </div>
              )}
              <div className="mt-1 flex flex-col gap-0.5">
                {g.items.map((it) => {
                  const className = `group flex h-10 w-full cursor-pointer items-center gap-3 whitespace-nowrap rounded-lg px-[14px] text-left text-[14px] transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#8FB8E6] ${
                    it.active
                      ? "bg-[#EFF5FC] font-semibold text-[#1F5596]"
                      : "bg-transparent font-medium text-[#475467] hover:bg-[#F4F6F9] hover:text-[#101828]"
                  }`;
                  const content = (
                    <>
                      <span
                        className={`flex h-5 w-5 flex-none items-center justify-center transition-colors duration-150 [&>svg]:h-[19px] [&>svg]:w-[19px] ${
                          it.active ? "text-[#2667B5]" : "text-[#667085] group-hover:text-[#344054]"
                        }`}
                      >
                        {it.icon}
                      </span>
                      <span className="truncate" style={fade}>
                        {it.label}
                      </span>
                    </>
                  );
                  if (renderItem) return renderItem(it, { className, content });
                  return (
                    <button
                      key={it.key}
                      type="button"
                      onClick={it.onClick}
                      aria-label={it.label}
                      aria-current={it.active ? "page" : undefined}
                      className={className}
                    >
                      {content}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </nav>
      </aside>
    </div>
  );
}

export function AccountMenu({ email, subtitle, onSignOut }) {
  const { open, setOpen, ref } = usePopover();
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account"
        data-testid="account-menu-trigger"
        className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full bg-[#1F5596] text-[13px] font-semibold text-white ring-offset-2 transition-shadow duration-150 hover:ring-2 hover:ring-[#BBD4F1]"
      >
        {(email || "?")[0].toUpperCase()}
      </button>
      {open && (
        <div className={`${MENU_CLASS} right-0 w-[260px]`}>
          <div className="px-[10px] pb-2 pt-[6px]">
            <div className="truncate text-[13px] font-medium text-[#111827]">{email}</div>
            {subtitle && <div className="mt-px text-[11px] text-[#6B7280]">{subtitle}</div>}
          </div>
          <div className="mx-1 my-[6px] h-px bg-[#F0F1F3]" />
          <button
            type="button"
            onClick={onSignOut}
            data-testid="logout-button"
            className="flex w-full cursor-pointer items-center gap-2 rounded-lg px-[10px] py-[9px] text-left text-[13px] font-medium text-[#4B5563] transition-colors duration-150 hover:bg-[#F4F6F9] hover:text-[#111827]"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4" />
              <path d="M16 17l5-5-5-5" />
              <path d="M21 12H9" />
            </svg>
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
